import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Partner {
	id: string
	cocode: number
	type: 'supplier' | 'customer'
	code: string
	name: string
	nameAr?: string
	contact?: {
		phone?: string
		mobile?: string
		email?: string
		fax?: string
	}
	address?: {
		street?: string
		city?: string
		country?: string
		postalCode?: string
	}
	accounting?: {
		glAccountCode?: string
		creditLimit?: number
		currentBalance?: number
		paymentTerms?: string
		taxNumber?: string
	}
	notes?: string
	isActive: boolean
	createdAt?: string
	updatedAt?: string
}

export interface CreatePartnerData {
	type: 'supplier' | 'customer'
	code: string
	name: string
	nameAr?: string
	contact?: {
		phone?: string
		mobile?: string
		email?: string
		fax?: string
	}
	address?: {
		street?: string
		city?: string
		country?: string
		postalCode?: string
	}
	accounting?: {
		glAccountCode?: string
		creditLimit?: number
		currentBalance?: number
		paymentTerms?: string
		taxNumber?: string
	}
	notes?: string
	isActive?: boolean
}

export interface UpdatePartnerData {
	name?: string
	nameAr?: string
	contact?: {
		phone?: string
		mobile?: string
		email?: string
		fax?: string
	}
	address?: {
		street?: string
		city?: string
		country?: string
		postalCode?: string
	}
	accounting?: {
		glAccountCode?: string
		creditLimit?: number
		currentBalance?: number
		paymentTerms?: string
		taxNumber?: string
	}
	notes?: string
	isActive?: boolean
}

interface PartnersResponse {
	data: Partner[]
	pagination: {
		page: number
		limit: number
		total: number
		totalPages: number
	}
}

interface PartnersParams {
	page?: number
	limit?: number
	type?: 'supplier' | 'customer'
	search?: string
	isActive?: boolean
}

export const usePartners = (params: PartnersParams = {}) => {
	const { page = 1, limit = 20, type, search, isActive } = params

	return useQuery({
		queryKey: ['partners', { page, limit, type, search, isActive }],
		queryFn: () =>
			apiClient.get<PartnersResponse>('/partners', {
				page,
				limit,
				type,
				search,
				isActive,
			}),
	})
}

export const usePartner = (id: string) => {
	return useQuery({
		queryKey: ['partner', id],
		queryFn: () => apiClient.get<Partner>(`/partners/${id}`),
		enabled: !!id,
	})
}

export const useCreatePartner = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: CreatePartnerData) =>
			apiClient.post<Partner>('/partners', data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['partners'] })
		},
	})
}

export const useUpdatePartner = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdatePartnerData }) =>
			apiClient.patch<Partner>(`/partners/${id}`, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ['partners'] })
			queryClient.invalidateQueries({ queryKey: ['partner', variables.id] })
		},
	})
}

export const useDeletePartner = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (id: string) => apiClient.delete(`/partners/${id}`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['partners'] })
		},
	})
}
